import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircle,
  faRedo,
  faTriangleExclamation,
  faUndo,
  faChevronDown,
  faChevronRight,
  faSearch,
  faFilter,
  faSort,
  faTrash,
  faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  setAll,
  setTemplateName,
  setYear as setTemplateYear,
  setTemplateProgramType,
  undo,
  redo,
  setTemplateId,
  clearHistory,
} from 'state/slices/templateSlice';
import { setYear as setCatalogYear } from 'state/slices/settingsSlice';
import { templateSave, templateGet, templateUpdate } from 'apis/templateAPIs';
import { checksheetGetAllByTemplate, checksheetGetAllProposedByYear } from 'apis/checksheetAPIs';
import { fetchDplUndergradMinorsCertificates } from 'apis/dplAPIs';
import { componentUpdate } from 'apis/componentAPIs';

import StaticData from './StaticData';
import { classNames } from 'utils';

import { Button } from '@asu/components-core/dist/libCore.es';
import Component from './Component';
import AddComponentDropdown from './AddComponentDropdown';
import TabPanel from './TabPanel';

import { useQuery } from '@tanstack/react-query';
import useServiceAuth from 'auth/useServiceAuth';
import useSafeMutation from 'auth/useSafeMutation';
import {
  cleanupSessionStorage,
  showRestorationAlert,
  parseSessionData,
  getCurrentUserFromToken,
} from 'auth/ServiceAuthUtils';
import TemplateFormTextOptions from './TemplateFormTextOptions';
import TemplateFormValidationRules from './TemplateFormValidationRules';
import LoadingSkeleton from './LoadingSkeleton';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import Modal from 'react-bootstrap/Modal';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownCustomToggle from './DropdownCustomToggle';
import StatusDefinitions from './StatusDefinitions';

import { useNavigate } from 'react-router-dom';

const stripHtmlTags = (html) => {
  if (!html) return '';
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const ComponentLoadingSkeleton = () => {
  return (
    <div className="mt-5">
      <div className="d-flex justify-content-between mb-2">
        <LoadingSkeleton width={'500px'} height={'40px'} />
        <LoadingSkeleton width={'340px'} height={'40px'} />
      </div>
      <div className="d-flex gap-8 mb-2">
        <LoadingSkeleton width={'100px'} height={'40px'} />
        <LoadingSkeleton width={'170px'} height={'40px'} />
      </div>
      {[...Array(5)].map((item, index) => (
        <LoadingSkeleton key={index} width={'100%'} height={'40px'} />
      ))}
    </div>
  );
};

const SaveAlert = ({
  templateName,
  succeeded,
  error = null,
  show,
  onClose,
}) => {
  return (
    <Toast
      show={show}
      onClose={onClose}
      className="bg-white"
      style={{ width: 'fit-content' }}
      delay={5000}
      autohide
    >
      <Toast.Body>
        <div className="d-flex align-items-center justify-content-between p-1">
          <div className="d-flex align-items-center gap-1">
            <FontAwesomeIcon
              icon={succeeded ? faCircleCheck : faTriangleExclamation}
              className={classNames(succeeded ? 'text-success' : 'text-danger')}
              style={{ width: '16px', height: '16px' }}
            />
            <div>
              {succeeded ? (
                <span>Template '{templateName}' has been saved.</span>
              ) : (
                <span>
                  Template was not saved.
                  <br />
                  Error: {error}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="btn-close ms-3"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
      </Toast.Body>
    </Toast>
  );
};

const TemplateForm = ({ tempId }) => {
  const { getAccessToken } = useServiceAuth();
  const { executeMutation: safeTemplateSave } = useSafeMutation(
    templateSave,
    'template-form-data',
    { flagKey: 'template-form-should-restore' }
  );
  const { executeMutation: safeTemplateUpdate } = useSafeMutation(
    templateUpdate,
    'template-form-data',
    { flagKey: 'template-form-should-restore' }
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [temporaryName, setTemporaryName] = useState('');
  const [timesSaved, setTimesSaved] = useState(0);
  const [showEditTemplateName, setShowEditTemplateName] = useState({
    hover: false,
    edit: false,
  });
  const [error, setError] = useState('');
  const [lockSave, setLockSave] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const [dataWasRestored, setDataWasRestored] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [collapsedColleges, setCollapsedColleges] = useState({});
  const [showChecksheetList, setShowChecksheetList] = useState(false);
  const [checksheetSearchString, setChecksheetSearchString] = useState('');
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [statusFilters, setStatusFilters] = useState([]);
  const [statusLabel, setStatusLabel] = useState('Status');
  const [departmentFilters, setDepartmentFilters] = useState([]);
  const [departmentLabel, setDepartmentLabel] = useState('Department');
  const [locationFilters, setLocationFilters] = useState([]);
  const [locationLabel, setLocationLabel] = useState('Location');
  const [checksheetSortBy, setChecksheetSortBy] = useState('nameA-Z');

  const templateId = useSelector((state) => state.template.templateId);
  const templateYear = useSelector((state) => state.template.year);
  const catalogYear = useSelector((state) => state.settings.year);
  const templateName = useSelector((state) => state.template.templateName);
  const templateProgramType = useSelector(
    (state) => state.template.templateProgramType
  );
  const templateSettings = useSelector(
    (state) => state.template.templateSettings
  );
  const templateComponentIds = useSelector(
    (state) => state.template.templateComponentIds || []
  );
  const templateComponentObjects = useSelector(
    (state) => state.template.templateComponentObjects
  );
  const templateValidationList = useSelector(
    (state) => state.template.templateValidationList || {}
  );
  const templateTextOptions = useSelector(
    (state) => state.template.templateTextOptions || {}
  );

  const templatePastState = useSelector((state) => state.template.past || []);
  const templateFutureState = useSelector((state) => state.template.future);
  const currentAction = useSelector((state) => state.template.currentAction);
  const futureAction = useSelector((state) => state.template.futureAction);

  // saveTemplate will be called directly using safeTemplateSave
  // No need for separate useMutation wrapper

  // updateTemplate will be called directly using safeTemplateUpdate
  // No need for separate useMutation wrapper

  const {
    data: templateData,
    error: getTemplateError,
    isError: getTemplateIsError,
    isPending: getTemplateIsLoading,
    fetchStatus: getTemplateFetchStatus,
    isSuccess: getTemplateIsSuccess,
    refetch,
  } = useQuery({
    queryKey: ['template', tempId],
    queryFn: async () => {
      const token = await getAccessToken();
      return templateGet({ id: tempId, token });
    },
    enabled: !!tempId,
  });

  const { data: templateChecksheets = [] } = useQuery({
    queryKey: ['template-checksheets', tempId || templateId],
    queryFn: async () => {
      const id = tempId || templateId;
      const token = await getAccessToken();
      const checksheets = await checksheetGetAllByTemplate({
        templateId: id,
        token,
      });
      return Array.isArray(checksheets) ? checksheets : [];
    },
    enabled: !!(tempId || templateId),
  });

  const checksheetCount = templateChecksheets.length;

  const { data: dplPrograms = [] } = useQuery({
    queryKey: ['dplPrograms'],
    queryFn: fetchDplUndergradMinorsCertificates,
  });

  const { data: proposedByYear = [] } = useQuery({
    queryKey: ['proposedByYear', templateYear || catalogYear],
    queryFn: async () => {
      const token = await getAccessToken();
      return checksheetGetAllProposedByYear({
        year: templateYear || catalogYear,
        token,
      });
    },
    enabled: !!(templateYear || catalogYear),
  });

  const handleSelectChangeYear = (e) => {
    dispatch(setCatalogYear(e.target.value));
    navigate('/templates');
  };

  // Restore saved template data on mount (from useSafeMutation)
  useEffect(() => {
    const savedData = sessionStorage.getItem('template-form-data');
    const shouldRestore = sessionStorage.getItem(
      'template-form-should-restore'
    );
    const componentModalReopen = sessionStorage.getItem(
      'component-modal-reopen'
    );

    console.log(
      '[TemplateForm] Restoration check:',
      '\n  template-form-data exists:',
      !!savedData,
      '\n  template-form-should-restore:',
      shouldRestore,
      '\n  component-modal-reopen:',
      componentModalReopen
    );

    // If component modal is being reopened, let ComponentForm handle ALL restoration (both component and template)
    if (componentModalReopen) {
      console.log(
        '[TemplateForm] ComponentForm modal reopening - skipping template restoration (ComponentForm will restore both)'
      );
      // Set flags to prevent fetch from overwriting ComponentForm's restoration and enable Save button
      setDataWasRestored(true);
      setTimesSaved(1);
      return;
    }

    // Only restore if we have both saved data AND the restore flag
    if (savedData && shouldRestore === 'true') {
      try {
        const parsedData = parseSessionData('template-form-data');
        console.log('Parsed saved template data:', parsedData);

        // Restore template through Redux setAll action
        dispatch(
          setAll({
            templateName: parsedData.templateName || '',
            templateProgramType: parsedData.programType || '',
            year: parsedData.year || catalogYear,
            templateId: parsedData.templateId || '',
            templateComponentIds: parsedData.componentIds || [],
            templateComponentObjects: parsedData.componentObjects || {},
            templateSettings: parsedData.settings || {},
            templateValidationList: parsedData.validationRules || {},
            templateTextOptions: parsedData.textOptions || {},
          })
        );

        setTemporaryName(parsedData.templateName || '');

        // Set flag to prevent fetch from overwriting restored data
        setDataWasRestored(true);

        // Enable Save button by setting timesSaved
        setTimesSaved(1);

        // Show restoration alert
        showRestorationAlert({
          formType: 'template',
        });

        // Clean up both the data and the flag
        cleanupSessionStorage(
          ['template-form-data', 'template-form-should-restore'],
          'TemplateForm restoration'
        );
      } catch (error) {
        console.error('Error restoring saved template data:', error);
      }
    }
  }, [dispatch, catalogYear]);

  // Proactively save template data to sessionStorage when modified
  // This protects against auth failures that occur outside of save mutations (e.g., when fetching components)
  useEffect(() => {
    // Skip if we're still loading initial data or if data was just restored
    if (!templateId || dataWasRestored) {
      return;
    }

    // Skip on initial mount before template is fully loaded
    if (
      timesSaved === 0 &&
      templateComponentObjects &&
      Object.keys(templateComponentObjects).length === 0
    ) {
      return;
    }

    const dataToSave = {
      templateName,
      programType: templateProgramType,
      year: catalogYear,
      templateId,
      componentIds: templateComponentIds,
      componentObjects: templateComponentObjects,
      settings: templateSettings,
      validationRules: templateValidationList,
      textOptions: templateTextOptions,
      _savedByUser: getCurrentUserFromToken(),
      _savedAt: new Date().toISOString(),
    };

    sessionStorage.setItem('template-form-data', JSON.stringify(dataToSave));
    console.log(
      '[TemplateForm] Proactively saved template data to session storage'
    );

    // Cleanup function: Clear saved data when component unmounts
    // UNLESS we're in the middle of an auth redirect (indicated by the restore flag)
    return () => {
      const shouldRestore = sessionStorage.getItem(
        'template-form-should-restore'
      );

      if (shouldRestore !== 'true') {
        // Normal navigation away - clear stale data
        console.log(
          '[TemplateForm] Component unmounting - clearing saved template data'
        );
        sessionStorage.removeItem('template-form-data');
      } else {
        // Auth redirect in progress - keep the data for restoration
        console.log(
          '[TemplateForm] Auth redirect in progress - preserving saved data'
        );
      }
    };
  }, [
    templateName,
    templateProgramType,
    catalogYear,
    templateId,
    templateComponentIds,
    templateComponentObjects,
    templateSettings,
    templateValidationList,
    templateTextOptions,
    dataWasRestored,
    timesSaved,
  ]); // Run once on mount

  const handleSaveTemplate = useCallback(async () => {
    // Validate template name
    const trimmedName = templateName ? templateName.trim() : '';
    if (!trimmedName) {
      setError('A template name is required');
      setShowAlert(true);
      setSaveSucceeded(false);
      return;
    }

    const jsonData = {
      year: templateId === '' ? catalogYear : templateYear,
      templateName: templateName,
      programType: templateProgramType,
      settings: templateSettings,
      validationRules: templateValidationList,
      textOptions: templateTextOptions,
      components: templateComponentIds,
    };
    console.log('JSON: ', jsonData);

    try {
      setLockSave(true);

      // Prepare data to save for restoration
      const dataToSave = {
        templateName,
        programType: templateProgramType,
        componentIds: templateComponentIds,
        componentObjects: templateComponentObjects,
        settings: templateSettings,
        validationRules: templateValidationList,
        textOptions: templateTextOptions,
        templateId: templateId || null,
        year: templateId === '' ? catalogYear : templateYear,
        jsonData,
      };

      if (templateId !== '') {
        // Update existing template
        const result = await safeTemplateUpdate(
          { id: templateId, jsonData },
          dataToSave
        );
        if (result) {
          setSaveSucceeded(true);
          setShowAlert(true);
          setLockSave(false);
        }
      } else {
        // Save new template
        const result = await safeTemplateSave({ jsonData }, dataToSave);
        if (result) {
          const temp = result.split(' ')[2].trim();
          const id = temp.substring(9, temp.length);
          console.log('ID: ', id);
          dispatch(setTemplateId(id));
          dispatch(setTemplateYear(catalogYear));
          setSaveSucceeded(true);
          setShowAlert(true);
          setLockSave(false);
        }
      }

      setTimesSaved(timesSaved + 1);
    } catch (err) {
      console.error('Error saving template:', err);
      // Don't show error alert - useSafeMutation handles auth failures
      // Only show alert for non-auth errors
      if (
        err.message &&
        !err.message.includes('401') &&
        !err.message.includes('token')
      ) {
        setError(err.message || 'Failed to save template');
        setShowAlert(true);
        setSaveSucceeded(false);
      }
      setLockSave(false);
    }
  }, [
    templateName,
    templateId,
    templateYear,
    templateProgramType,
    templateSettings,
    templateValidationList,
    templateTextOptions,
    templateComponentIds,
    templateComponentObjects,
    catalogYear,
    safeTemplateSave,
    safeTemplateUpdate,
    dispatch,
    timesSaved,
    setSaveSucceeded,
    setShowAlert,
    setError,
    setLockSave,
    setTimesSaved,
  ]);

  useEffect(() => {
    console.log('This is running');
    // Skip fetch if we just restored data from sessionStorage
    if (dataWasRestored) {
      console.log('Skipping fetch - data was restored from sessionStorage');
      return;
    }

    if (tempId !== '' && tempId !== null) {
      console.log('Refetching...');
      refetch();

      if (getTemplateIsError) setError(getTemplateError.message);

      if (getTemplateIsSuccess) {
        console.log(templateData);

        const jsonDetails = JSON.parse(templateData['details']);

        dispatch(
          setAll({
            templateId: templateData['sk'],
            year: jsonDetails.year,
            templateName: jsonDetails.templateName,
            templateProgramType: jsonDetails.programType ?? '',
            templateSettings: jsonDetails.settings,
            templateComponentIds: jsonDetails.components,
            templateComponentObjects: templateData['componentObjects'],
            templateValidationList: jsonDetails.validationRules,
            templateTextOptions: jsonDetails.textOptions,
          })
        );
        setTemporaryName(jsonDetails.templateName);
      }
    }
  }, [
    dispatch,
    getTemplateError,
    getTemplateIsError,
    getTemplateIsSuccess,
    tempId,
    refetch,
    templateData,
    dataWasRestored,
  ]);

  useEffect(() => {
    setTemporaryName(templateName);
  }, [templateName]);

  // Keyboard shortcut: Ctrl+S / Cmd+S to save
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        if (!lockSave) {
          handleSaveTemplate();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSaveTemplate, lockSave]);

  // Build a map of acadPlanCode -> DPL program info for quick lookup
  const dplProgramMap = React.useMemo(() => {
    const map = {};
    dplPrograms.forEach((program) => {
      map[program.acadPlanCode] = program;
    });
    return map;
  }, [dplPrograms]);

  // Build a map of sk -> checksheetName from proposed checksheets
  const proposedNameMap = React.useMemo(() => {
    const map = {};
    if (Array.isArray(proposedByYear)) {
      proposedByYear.forEach((cs) => {
        const sk = cs.sk?.S || cs.sk || '';
        const name = cs.checksheetName?.S || cs.checksheetName || '';
        if (sk && name) map[sk] = name;
      });
    }
    return map;
  }, [proposedByYear]);

  // Group checksheets by college using DPL program data
  const { checksheetsByCollege, proposedChecksheets } = React.useMemo(() => {
    const grouped = {};
    const proposed = [];
    templateChecksheets.forEach((cs) => {
      // sk format: {year}*{college}*{acadplan}*{subplan|null}
      const sk = cs.sk?.S || cs.sk || '';
      const parts = sk.split('*');
      if (parts.length < 3) return;
      const acadplan = parts[2];
      const subplan = parts[3] && parts[3] !== 'null' ? parts[3] : null;

      const status = cs.progress?.S || cs.progress || '';

      const dplProgram = dplProgramMap[acadplan];

      // If not found in DPL, it's a proposed checksheet
      if (!dplProgram) {
        proposed.push({
          programName: proposedNameMap[sk] || acadplan,
          degreeShort: '',
          acadplan,
          subplan,
          collegeCode: parts[1] || '',
          status,
        });
        return;
      }

      let collegeName = parts[1] || 'Unknown';
      let programName = dplProgram.acadPlanMarketingDescription || dplProgram.acadPlanDescription || acadplan;
      let degreeShort = dplProgram.degreeDescriptionShort || '';
      // Get college name from owners
      if (dplProgram.owners && dplProgram.owners.length > 0) {
        let mainOwner = dplProgram.owners[0];
        let highestPercent = 0;
        dplProgram.owners.forEach((owner) => {
          if (owner.percentOwned > highestPercent) {
            highestPercent = owner.percentOwned;
            mainOwner = owner;
          }
        });
        collegeName = mainOwner.collegeDescription || collegeName;
      }

      if (!grouped[collegeName]) grouped[collegeName] = [];
      grouped[collegeName].push({
        programName,
        degreeShort,
        acadplan,
        subplan,
        status,
        department: dplProgram.owners?.[0]?.departmentDescription || '',
        departmentCode: dplProgram.owners?.[0]?.departmentAcadOrg || '',
        campuses: dplProgram.campusesOffered || [],
      });
    });
    // Sort colleges and programs
    const sorted = {};
    Object.keys(grouped).sort().forEach((college) => {
      sorted[college] = grouped[college].sort((a, b) =>
        a.programName.localeCompare(b.programName)
      );
    });
    return {
      checksheetsByCollege: sorted,
      proposedChecksheets: proposed.sort((a, b) =>
        a.acadplan.localeCompare(b.acadplan)
      ),
    };
  }, [templateChecksheets, dplProgramMap, proposedNameMap]);

  // Build department list from all programs for the filter dropdown
  const checksheetDepartments = React.useMemo(() => {
    const deptMap = {};
    Object.values(checksheetsByCollege).forEach((programs) => {
      programs.forEach((p) => {
        if (p.departmentCode && !deptMap[p.departmentCode]) {
          deptMap[p.departmentCode] = p.department || p.departmentCode;
        }
      });
    });
    return Object.entries(deptMap).sort((a, b) => a[1].localeCompare(b[1]));
  }, [checksheetsByCollege]);

  const toggleCollege = (college) => {
    setCollapsedColleges((prev) => ({
      ...prev,
      [college]: !prev[college],
    }));
  };

  const handleFilterStatus = (e) => {
    const value = e.target.value;
    const current = [...statusFilters];
    if (current.includes(value)) {
      current.splice(current.indexOf(value), 1);
    } else {
      current.push(value);
    }
    if (current.length === 0) setStatusLabel('Status');
    else if (current.length === 1) {
      setStatusLabel(StaticData.statusArray[current[0]] || current[0]);
    } else setStatusLabel(`Status (${current.length})`);
    setStatusFilters(current);
  };

  const handleUncheckAllStatuses = () => {
    setStatusLabel('Status');
    setStatusFilters([]);
  };

  const handleFilterDepartment = (e) => {
    const value = e.target.value;
    const current = [...departmentFilters];
    if (current.includes(value)) {
      current.splice(current.indexOf(value), 1);
    } else {
      current.push(value);
    }
    if (current.length === 0) setDepartmentLabel('Department');
    else if (current.length === 1) {
      const dept = checksheetDepartments.find((d) => d[0] === current[0]);
      setDepartmentLabel(dept ? dept[1] : current[0]);
    } else setDepartmentLabel(`Department (${current.length})`);
    setDepartmentFilters(current);
  };

  const handleUncheckAllDepartments = () => {
    setDepartmentLabel('Department');
    setDepartmentFilters([]);
  };

  const handleFilterLocation = (e) => {
    const value = e.target.value;
    const current = [...locationFilters];
    if (current.includes(value)) {
      current.splice(current.indexOf(value), 1);
    } else {
      current.push(value);
    }
    if (current.length === 0) setLocationLabel('Location');
    else if (current.length === 1) {
      const campus = StaticData.campusUndergradList.find(
        (c) => c.value === current[0]
      );
      setLocationLabel(campus ? campus.label : current[0]);
    } else setLocationLabel(`Location (${current.length})`);
    setLocationFilters(current);
  };

  const handleUncheckAllLocations = () => {
    setLocationLabel('Location');
    setLocationFilters([]);
  };

  const handleClearAllChecksheetFilters = () => {
    handleUncheckAllStatuses();
    handleUncheckAllDepartments();
    handleUncheckAllLocations();
  };

  // Compute items changed by category from undo history
  // Compare current state against the original (first past entry) to find actual diffs
  const itemsChanged = React.useMemo(() => {
    if (templatePastState.length === 0) return { sections: 0, validationRules: 0, textOptions: 0 };
    const original = templatePastState[0];
    const counts = { sections: 0, validationRules: 0, textOptions: 0 };

    // Sections: compare componentIds and componentObjects
    if (JSON.stringify(original.templateComponentIds) !== JSON.stringify(templateComponentIds) ||
        JSON.stringify(original.templateComponentObjects) !== JSON.stringify(templateComponentObjects)) {
      counts.sections = 1;
    }

    // Validation Rules: compare settings and validationList
    if (JSON.stringify(original.templateSettings) !== JSON.stringify(templateSettings) ||
        JSON.stringify(original.templateValidationList) !== JSON.stringify(templateValidationList)) {
      counts.validationRules = 1;
    }

    // Text Options: compare textOptions
    if (JSON.stringify(original.templateTextOptions) !== JSON.stringify(templateTextOptions)) {
      counts.textOptions = 1;
    }

    return counts;
  }, [templatePastState, templateComponentIds, templateComponentObjects, templateSettings, templateValidationList, templateTextOptions]);

  // Compute the specific changed validation fields for display
  const changedValidationFields = React.useMemo(() => {
    if (templatePastState.length === 0) return { settings: [], rules: [] };
    const original = templatePastState[0];
    const origSettings = original.templateSettings || {};
    const currSettings = templateSettings || {};
    const settingChanges = [];

    if (currSettings.hours !== origSettings.hours)
      settingChanges.push({ label: 'Total Credit Hours Min', value: currSettings.hours });
    if (currSettings.maxHours !== origSettings.maxHours)
      settingChanges.push({ label: 'Total Credit Hours Max', value: currSettings.maxHours });
    if (currSettings.upperDivisionHours !== origSettings.upperDivisionHours)
      settingChanges.push({ label: 'Upper Division', value: currSettings.upperDivisionHours });
    if (currSettings.gpa !== origSettings.gpa)
      settingChanges.push({ label: 'GPA Minimum', value: StaticData.gpaList.find((g) => g.value === currSettings.gpa)?.label || currSettings.gpa });
    if (currSettings.majorGpa !== origSettings.majorGpa)
      settingChanges.push({ label: 'Major GPA Minimum', value: StaticData.gpaList.find((g) => g.value === currSettings.majorGpa)?.label || currSettings.majorGpa });

    const origRules = original.templateValidationList || [];
    const currRules = templateValidationList || [];
    const ruleChanges = [];

    if (Array.isArray(currRules)) {
      currRules.forEach((rule) => {
        const origRule = Array.isArray(origRules)
          ? origRules.find((r) => r.category?.value === rule.category?.value)
          : null;
        const changes = [];
        if ((rule.hours || 0) !== (origRule?.hours || 0))
          changes.push({ label: 'Credit Hours Min', value: rule.hours });
        if ((rule.upperDivisionHours || 0) !== (origRule?.upperDivisionHours || 0))
          changes.push({ label: 'Upper Division Hours Min', value: rule.upperDivisionHours });
        if ((rule.gpa || 0) !== (origRule?.gpa || 0))
          changes.push({ label: 'GPA Min', value: StaticData.gpaList.find((g) => g.value === rule.gpa)?.label || rule.gpa });
        if (changes.length > 0) {
          ruleChanges.push({ category: rule.category, changes });
        }
      });
    }

    return { settings: settingChanges, rules: ruleChanges };
  }, [templatePastState, templateSettings, templateValidationList]);

  // Compute changed sections
  const changedSections = React.useMemo(() => {
    if (templatePastState.length === 0) return [];
    const original = templatePastState[0];
    const origIds = original.templateComponentIds || [];
    const origObjects = original.templateComponentObjects || {};
    const changes = [];

    // Helper to get component name from object
    const getComponentName = (obj) => {
      if (!obj) return '';
      if (obj.componentName) return obj.componentName;
      if (obj.details) {
        try {
          const details = typeof obj.details === 'string' ? JSON.parse(obj.details) : obj.details;
          if (details.S) return JSON.parse(details.S).componentName || '';
          return details.componentName || '';
        } catch (e) { return ''; }
      }
      return '';
    };

    // Added sections
    templateComponentIds.forEach((id) => {
      if (!origIds.includes(id)) {
        const name = getComponentName(templateComponentObjects?.[id]) || id;
        changes.push({ name, status: 'Added' });
      }
    });

    // Removed sections
    origIds.forEach((id) => {
      if (!templateComponentIds.includes(id)) {
        const name = getComponentName(origObjects?.[id]) || id;
        changes.push({ name, status: 'Removed' });
      }
    });

    // Existing sections: check for edits, renames, reorders
    templateComponentIds.forEach((id) => {
      if (!origIds.includes(id)) return; // already handled as Added
      const origObj = origObjects?.[id];
      const currObj = templateComponentObjects?.[id];
      if (!origObj || !currObj) return;

      const origName = getComponentName(origObj);
      const currName = getComponentName(currObj);

      const statuses = [];
      const contentChanged = JSON.stringify(origObj) !== JSON.stringify(currObj);
      const nameChanged = origName !== currName;
      const posChanged = origIds.indexOf(id) !== templateComponentIds.indexOf(id);

      if (contentChanged && !nameChanged) statuses.push('Edited');
      if (nameChanged) statuses.push('Renamed');
      if (nameChanged && contentChanged) statuses.push('Edited');
      if (posChanged) statuses.push('Reordered');

      // Deduplicate
      const uniqueStatuses = [...new Set(statuses)];
      if (uniqueStatuses.length > 0) {
        changes.push({ name: currName || id, status: uniqueStatuses.join(', ') });
      }
    });

    return changes;
  }, [templatePastState, templateComponentIds, templateComponentObjects]);

  // Compute changed text options with detailed diffs
  const changedTextOptions = React.useMemo(() => {
    if (templatePastState.length === 0) return [];
    const original = templatePastState[0];
    const origGroups = Array.isArray(original.templateTextOptions) ? original.templateTextOptions : [];
    const currGroups = Array.isArray(templateTextOptions) ? templateTextOptions : [];
    const changes = [];

    // Track which original groups have been matched
    const matchedOrigIndices = new Set();

    currGroups.forEach((group, idx) => {
      const origGroup = origGroups[idx];
      if (!origGroup) {
        // Entire group is new
        changes.push({
          groupName: group.groupName,
          groupStatus: 'Added',
          options: (group.options || []).map((opt) => ({
            text: opt.label || opt.value || opt,
            status: 'Added',
          })),
        });
        return;
      }

      matchedOrigIndices.add(idx);
      const groupNameChanged = origGroup.groupName !== group.groupName;
      const origOptions = origGroup.options || [];
      const currOptions = group.options || [];
      const optionChanges = [];

      // Build a map of original options by text for matching
      const origTexts = origOptions.map((o) => o.label || o.value || o);
      const currTexts = currOptions.map((o) => o.label || o.value || o);

      currOptions.forEach((opt, optIdx) => {
        const text = opt.label || opt.value || opt;
        const origIdx = origTexts.indexOf(text);
        if (origIdx === -1) {
          // Check if it's an edit (same position, different text)
          if (optIdx < origOptions.length) {
            const origText = origTexts[optIdx];
            if (!currTexts.includes(origText)) {
              optionChanges.push({ text, status: 'Edited' });
            } else {
              optionChanges.push({ text, status: 'Added' });
            }
          } else {
            optionChanges.push({ text, status: 'Added' });
          }
        } else if (origIdx !== optIdx) {
          // Same text but different position = reordered
          const origAtThisPos = optIdx < origTexts.length ? origTexts[optIdx] : null;
          if (origAtThisPos && origAtThisPos !== text && !currTexts.includes(origAtThisPos)) {
            optionChanges.push({ text, status: 'Edited, Reordered' });
          } else {
            optionChanges.push({ text, status: 'Reordered' });
          }
        }
      });

      // Removed options
      origOptions.forEach((opt) => {
        const text = opt.label || opt.value || opt;
        if (!currTexts.includes(text)) {
          // Check if it was edited (already counted above)
          const origIdx = origTexts.indexOf(text);
          const alreadyCounted = optionChanges.some(
            (c) => c.status.includes('Edited') && currOptions[origIdx]
          );
          if (!alreadyCounted) {
            optionChanges.push({ text, status: 'Removed' });
          }
        }
      });

      if (groupNameChanged || optionChanges.length > 0) {
        changes.push({
          groupName: group.groupName,
          groupStatus: groupNameChanged ? 'Renamed' : null,
          options: optionChanges,
        });
      }
    });

    // Removed groups
    origGroups.forEach((group, idx) => {
      if (!matchedOrigIndices.has(idx) && idx >= currGroups.length) {
        changes.push({
          groupName: group.groupName,
          groupStatus: 'Removed',
          options: [],
        });
      }
    });

    return changes;
  }, [templatePastState, templateTextOptions]);

  const textOptionsChangeCount = changedTextOptions.reduce(
    (sum, g) => sum + (g.groupStatus ? 1 : 0) + g.options.length, 0
  );

  const totalItemsChanged =
    changedSections.length +
    (changedValidationFields.settings.length + changedValidationFields.rules.reduce((sum, r) => sum + r.changes.length, 0)) +
    textOptionsChangeCount;

  // Filter checksheetsByCollege based on search string, status, department, location
  const filteredChecksheetsByCollege = React.useMemo(() => {
    const filtered = {};
    const q = checksheetSearchString.trim().toLowerCase();
    Object.entries(checksheetsByCollege).forEach(([college, programs]) => {
      const matchedPrograms = programs.filter((p) => {
        if (statusFilters.length > 0 && !statusFilters.includes(p.status)) return false;
        if (departmentFilters.length > 0 && !departmentFilters.includes(p.departmentCode)) return false;
        if (locationFilters.length > 0) {
          const hasCampus = p.campuses?.some((c) =>
            locationFilters.includes(c.campusCode || c)
          );
          if (!hasCampus) return false;
        }
        if (q && !(
          p.programName.toLowerCase().includes(q) ||
          p.acadplan.toLowerCase().includes(q) ||
          (p.degreeShort && p.degreeShort.toLowerCase().includes(q)) ||
          college.toLowerCase().includes(q)
        )) return false;
        return true;
      });
      const sorted = [...matchedPrograms].sort((a, b) => {
        if (checksheetSortBy === 'nameZ-A') return b.programName.localeCompare(a.programName);
        return a.programName.localeCompare(b.programName);
      });
      if (sorted.length > 0) {
        filtered[college] = sorted;
      }
    });
    return filtered;
  }, [checksheetsByCollege, checksheetSearchString, statusFilters, departmentFilters, locationFilters, checksheetSortBy]);

  const filteredProposedChecksheets = React.useMemo(() => {
    const q = checksheetSearchString.trim().toLowerCase();
    let filtered = proposedChecksheets.filter((p) => {
      if (statusFilters.length > 0 && !statusFilters.includes(p.status)) return false;
      if (q && !(
        p.programName.toLowerCase().includes(q) ||
        p.acadplan.toLowerCase().includes(q) ||
        p.collegeCode.toLowerCase().includes(q)
      )) return false;
      return true;
    });
    filtered.sort((a, b) => {
      if (checksheetSortBy === 'nameZ-A') return b.programName.localeCompare(a.programName);
      return a.programName.localeCompare(b.programName);
    });
    return filtered;
  }, [proposedChecksheets, checksheetSearchString, statusFilters, checksheetSortBy]);

  const filteredChecksheetTotal = Object.values(
    filteredChecksheetsByCollege
  ).reduce((sum, programs) => sum + programs.length, 0) + filteredProposedChecksheets.length;

  if (getTemplateIsError)
    return (
      <div>
        <div>An error has occurred: {error}</div>
        <div>
          Please refresh the page. If the error persists, please try again
          later.
        </div>
      </div>
    );

  return (
    <>
      <ToastContainer
        className="p-3"
        position="top-end"
        style={{ zIndex: 9999 }}
      >
        <SaveAlert
          templateName={templateName}
          succeeded={saveSucceeded}
          error={error}
          show={showAlert}
          onClose={() => setShowAlert(false)}
        />
      </ToastContainer>
      {!showChecksheetList && (
      <div
        className="template-header bg-gray-1 py-3 position-fixed"
        style={{ width: '80%', zIndex: 1 }}
      >
        <div className="container">
          <div className="row align-items-center">
            {getTemplateIsLoading && getTemplateFetchStatus === 'fetching' ? (
              <LoadingSkeleton width={'714px'} height={'40px'} />
            ) : (
              <div
                className="position-relative col-7"
                onMouseEnter={() => {
                  if (!showEditTemplateName.edit)
                    setTemporaryName(templateName);
                  setShowEditTemplateName({
                    ...showEditTemplateName,
                    hover: true,
                  });
                }}
                onMouseLeave={() =>
                  setShowEditTemplateName({
                    ...showEditTemplateName,
                    hover: false,
                  })
                }
              >
                <input
                  type="text"
                  className={classNames(
                    showEditTemplateName.hover ||
                      showEditTemplateName.edit ||
                      templateName.trim(' ') === ''
                      ? 'visible opacity-100'
                      : 'invisible opacity-0',
                    templateName.trim(' ') === ''
                      ? 'position-relative'
                      : 'template-name-input',
                    'w-100 fs-4 fw-bold px-2 py-1'
                  )}
                  value={temporaryName}
                  tabIndex={0}
                  onChange={(e) => {
                    if (e.target.value !== temporaryName)
                      setTemporaryName(e.target.value);
                  }}
                  onFocus={() =>
                    setShowEditTemplateName({
                      ...showEditTemplateName,
                      edit: true,
                    })
                  }
                  onBlur={() => {
                    setShowEditTemplateName({
                      ...showEditTemplateName,
                      edit: false,
                    });
                    if (temporaryName !== templateName) {
                      dispatch(setTemplateName(temporaryName));
                      // Auto-save the template name change
                      setTimeout(() => handleSaveTemplate(), 0);
                    }
                  }}
                />
                <div className="fs-4 fw-bold">{templateName}</div>
                {(tempId || templateId) && (
                  <div style={{ fontSize: '0.85rem' }}>
                    <span
                      className="text-maroon"
                      style={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => setShowChecksheetList(true)}
                      role="button"
                    >
                      {checksheetCount}{' '}
                      {checksheetCount === 1 ? 'checksheet' : 'checksheets'}
                    </span>{' '}
                    created from this template
                  </div>
                )}
              </div>
            )}
            <div className="col-5 d-flex justify-content-end gap-2">
              <div>
                <div className="fw-bold">Program Type</div>
                <select
                  value={templateProgramType}
                  onChange={(e) => {
                    dispatch(setTemplateProgramType(e.target.value));
                    setTimeout(() => handleSaveTemplate(), 0);
                  }}
                  className="text-gray-7 p-1"
                >
                  <option value="" disabled>
                    Select a program type
                  </option>
                  <option value="undergrad">Undergraduate degree</option>
                  <option value="minor">Minor program</option>
                  <option value="cert">Certificate program</option>
                  {/* <option value="ids">IDS Concentration</option> */}
                </select>
              </div>
              <div>
                <div className="fw-bold">Catalog Year</div>
                <select
                  value={catalogYear}
                  onChange={handleSelectChangeYear}
                  styles={{
                    width: '150px',
                  }}
                  className="text-gray-7 p-1"
                >
                  {StaticData.yearList.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <div className="d-flex">
                  <span
                    onClick={() => dispatch(undo())}
                    className="m-1"
                    title={
                      currentAction !== '' ? `Undo ${currentAction}` : 'Undo'
                    }
                    role="button"
                  >
                    <FontAwesomeIcon
                      icon={faUndo}
                      className={classNames(
                        !templatePastState.length && 'text-gray-4'
                      )}
                    />
                  </span>
                  <span
                    onClick={() => dispatch(redo())}
                    className="m-1"
                    title={
                      futureAction !== '' ? `Redo ${futureAction}` : 'Redo'
                    }
                    role="button"
                  >
                    <FontAwesomeIcon
                      icon={faRedo}
                      className={classNames(
                        !templateFutureState.length && 'text-gray-4'
                      )}
                    />
                  </span>
                </div>
                {totalItemsChanged > 0 ? (
                  <div className="d-flex flex-column align-items-center">
                    <span className="text-gray-5 text-nowrap mb-1" style={{ fontSize: '0.85rem' }}>
                      {totalItemsChanged} unpublished {totalItemsChanged === 1 ? 'change' : 'changes'}
                    </span>
                    <Button
                      label="Review and publish"
                      onClick={() => {
                        setCollapsedColleges((prev) => ({
                          ...prev,
                          '__modal_sections__': false,
                          '__modal_validation__': false,
                          '__modal_textoptions__': false,
                        }));
                        setShowPublishModal(true);
                      }}
                      color="gold"
                      size="small"
                      disabled={lockSave}
                      ariaLabel="Review and publish template"
                      title="Review and publish template"
                    />
                  </div>
                ) : (
                  <span className="text-gray-5 text-nowrap" style={{ fontSize: '0.85rem' }}>
                    0 unpublished changes
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
      <div className="container" style={{ paddingTop: showChecksheetList ? '20px' : '154px' }}>
        {showChecksheetList ? (
          <div className="row">
            <div className="col">
              <Button
                label="Back to template"
                onClick={() => {
                  setShowChecksheetList(false);
                  setChecksheetSearchString('');
                }}
                color="gold"
                size="small"
                icon={['fa', 'arrow-left']}
              />
              <div className="mb-3 mt-1">
                <div style={{ fontSize: '0.9rem' }}>
                  Checksheets created from
                </div>
                <h4 className="fw-bold mb-0">
                  {templateName}
                </h4>
                <div className="text-gray-6">
                  {Number(templateYear || catalogYear)} - {Number(templateYear || catalogYear) + 1}
                </div>
              </div>
              <div className="d-flex gap-6 justify-content-between mb-4">
                <div className="d-flex gap-3">
                  <div>
                    <div className="d-flex gap-1 align-items-center mb-1">
                      <FontAwesomeIcon icon={faSearch} />
                      <div className="fw-bold">Search</div>
                    </div>
                    <input
                      type="text"
                      placeholder="Search programs"
                      className="border-0 border-bottom border-gray-6 bg-transparent px-3 py-1"
                      style={{ width: '250px' }}
                      value={checksheetSearchString}
                      onChange={(e) =>
                        setChecksheetSearchString(e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <div className="d-flex gap-1 align-items-center mb-1">
                      <FontAwesomeIcon icon={faFilter} />
                      <div className="fw-bold">Filters</div>
                    </div>
                    <div className="filters d-flex flex-wrap gap-1 align-items-center">
                      <Dropdown>
                        <Dropdown.Toggle
                          as={DropdownCustomToggle}
                          id="dropdown-department-filter"
                        >
                          <Button
                            label={departmentLabel}
                            icon={['fa', 'chevron-down']}
                            classes={[
                              'btn',
                              departmentFilters.length > 0
                                ? 'btn-maroon'
                                : 'btn-white',
                              'btn-md',
                            ]}
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          className="py-0"
                          style={{ width: '540px' }}
                        >
                          <Dropdown.Item
                            className="button-link px-3 mt-2"
                            role="button"
                            onClick={handleUncheckAllDepartments}
                          >
                            Uncheck all
                          </Dropdown.Item>
                          {checksheetDepartments.map(([key, value]) => (
                            <Dropdown.ItemText
                              key={key}
                              className="d-flex gap-2 ps-3"
                              role="button"
                            >
                              <input
                                type="checkbox"
                                id={'checkbox-tpl-dept-' + key}
                                value={key}
                                checked={departmentFilters.includes(key)}
                                onChange={(e) => handleFilterDepartment(e)}
                              />
                              <span>{value}</span>
                            </Dropdown.ItemText>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Dropdown>
                        <Dropdown.Toggle
                          as={DropdownCustomToggle}
                          id="dropdown-location-filter"
                        >
                          <Button
                            label={locationLabel}
                            icon={['fa', 'chevron-down']}
                            classes={[
                              'btn',
                              locationFilters.length > 0
                                ? 'btn-maroon'
                                : 'btn-white',
                              'btn-md',
                            ]}
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          className="py-0"
                          style={{ width: '340px' }}
                        >
                          <Dropdown.Item
                            className="button-link px-3 mt-2"
                            role="button"
                            onClick={handleUncheckAllLocations}
                          >
                            Uncheck all
                          </Dropdown.Item>
                          {StaticData.campusUndergradList.map((campus) => (
                            <Dropdown.ItemText
                              key={campus.value}
                              className="d-flex gap-2 ps-3"
                              role="button"
                            >
                              <input
                                type="checkbox"
                                id={'checkbox-tpl-loc-' + campus.value}
                                value={campus.value}
                                checked={locationFilters.includes(campus.value)}
                                onChange={(e) => handleFilterLocation(e)}
                              />
                              <span>{campus.label}</span>
                            </Dropdown.ItemText>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      <Dropdown>
                        <Dropdown.Toggle
                          as={DropdownCustomToggle}
                          id="dropdown-status-filter"
                        >
                          <Button
                            label={statusLabel}
                            icon={['fa', 'chevron-down']}
                            classes={[
                              'btn',
                              statusFilters.length > 0
                                ? 'btn-maroon'
                                : 'btn-white',
                              'btn-md',
                            ]}
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          className="py-0"
                          style={{ width: '340px' }}
                        >
                          <Dropdown.Item
                            className="button-link px-3 mt-2"
                            role="button"
                            onClick={handleUncheckAllStatuses}
                          >
                            Uncheck all
                          </Dropdown.Item>
                          {StaticData.statusList.map((status) => (
                            <Dropdown.ItemText
                              key={status.value}
                              className="d-flex gap-2 ps-3"
                              role="button"
                            >
                              <input
                                type="checkbox"
                                id={'checkbox-tpl-' + status.value}
                                value={status.value}
                                checked={statusFilters.includes(status.value)}
                                onChange={(e) => handleFilterStatus(e)}
                              />
                              <span>{status.label}</span>
                            </Dropdown.ItemText>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                      {(departmentFilters.length > 0 ||
                        locationFilters.length > 0 ||
                        statusFilters.length > 0) && (
                        <div
                          className="button-link"
                          onClick={handleClearAllChecksheetFilters}
                          role="button"
                        >
                          Clear all filters
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="d-flex gap-1 align-items-center mb-1">
                    <FontAwesomeIcon icon={faSort} />
                    <div className="fw-bold">Sort by</div>
                  </div>
                  <select
                    value={checksheetSortBy}
                    onChange={(e) => setChecksheetSortBy(e.target.value)}
                  >
                    <option value="nameA-Z">Name (A-Z)</option>
                    <option value="nameZ-A">Name (Z-A)</option>
                  </select>
                </div>
              </div>
              <div className="mb-3 text-gray-6">
                {filteredChecksheetTotal} checksheets
              </div>
              {Object.entries(filteredChecksheetsByCollege).map(
                ([college, programs]) => (
                  <div key={college} className="mb-1">
                    <div
                      className={classNames(
                        'd-flex align-items-center gap-2 py-2 border-bottom college-hover-link',
                        collapsedColleges[college] && 'text-maroon'
                      )}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleCollege(college)}
                      role="button"
                    >
                      <FontAwesomeIcon
                        icon={
                          collapsedColleges[college]
                            ? faChevronDown
                            : faChevronRight
                        }
                        style={{ width: '12px' }}
                      />
                      <span className="fw-bold">{college}</span>
                      <span
                        className="badge rounded-pill bg-maroon text-white"
                        style={{ fontSize: '0.75rem' }}
                      >
                        {programs.length}
                      </span>
                    </div>
                    {collapsedColleges[college] && (
                      <div>
                        <div className="row border-bottom border-gray-4 py-2 mx-0 fw-bold" style={{ fontSize: '0.85rem' }}>
                          <div className="col-3">Name</div>
                          <div className="col-2">Plan code</div>
                          <div className="col-2">Department</div>
                          <div className="col-3">Locations</div>
                          <div className="col-2">Status <FontAwesomeIcon icon={faCircleInfo} className="ms-1" onClick={() => setShowDefinitions(true)} role="button" /></div>
                        </div>
                        {programs.map((program, idx) => (
                          <div
                            key={idx}
                            className="row border-bottom border-gray-2 py-2 mx-0 align-items-center"
                            style={{ fontSize: '0.85rem' }}
                          >
                            <div className="col-3 fw-bold">
                              {program.programName}
                              {program.degreeShort
                                ? `, ${program.degreeShort}`
                                : ''}
                            </div>
                            <div className="col-2">{program.acadplan}{program.subplan ? `/${program.subplan}` : ''}</div>
                            <div className="col-2">{program.department || ''}</div>
                            <div className="col-3">
                              {program.campuses?.map((c) => {
                                const found = StaticData.campusUndergradList.find(
                                  (cu) => cu.value === c.campusCode
                                );
                                return found?.label || c.campusCode || '';
                              }).join(', ') || ''}
                            </div>
                            <div className="col-2 d-flex align-items-center gap-1">
                              {program.status && (
                                <>
                                  <FontAwesomeIcon
                                    icon={faCircle}
                                    className={
                                      program.status === 'COMPLETE' ? 'text-success' :
                                      program.status === 'DARS' ? 'text-info' :
                                      program.status === 'PROVOST' ? 'text-warning' :
                                      program.status === 'COLLEGE' ? 'text-gray-7' :
                                      'text-gray-4'
                                    }
                                    style={{ width: '10px' }}
                                  />
                                  <span>{StaticData.statusArray[program.status] || program.status}</span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}
              {filteredProposedChecksheets.length > 0 && (
                <div className="mb-1">
                  <div
                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollege('__proposed__')}
                    role="button"
                  >
                    <FontAwesomeIcon
                      icon={
                        collapsedColleges['__proposed__']
                          ? faChevronDown
                          : faChevronRight
                      }
                      style={{ width: '12px' }}
                    />
                    <span className="fw-bold">Proposed checksheets</span>
                    <span
                      className="badge rounded-pill bg-maroon text-white"
                      style={{ fontSize: '0.75rem' }}
                    >
                      {filteredProposedChecksheets.length}
                    </span>
                  </div>
                  {collapsedColleges['__proposed__'] && (
                    <div className="ps-4">
                      {filteredProposedChecksheets.map((program, idx) => (
                        <div
                          key={idx}
                          className="py-1 ps-2"
                          style={{ fontSize: '0.9rem' }}
                        >
                          {program.programName}
                          {program.programName !== program.acadplan
                            ? ` (${program.acadplan})`
                            : ''}
                          {program.subplan
                            ? `/${program.subplan}`
                            : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
        <div className="row">
          <div className="col">
            <TabPanel tabs={['Sections', 'Validation Rules', 'Text Options']}>
              <div className="pt-2 mt-2">
                <div className="d-flex align-items-center gap-2">
                  <AddComponentDropdown />
                  {getTemplateIsLoading &&
                  getTemplateFetchStatus === 'fetching' ? (
                    <LoadingSkeleton width={'150px'} height={'20'} />
                  ) : (
                    <div>
                      {templateComponentIds.length}{' '}
                      {templateComponentIds.length === 1
                        ? 'section'
                        : 'sections'}{' '}
                      added
                    </div>
                  )}
                </div>
                {getTemplateIsLoading &&
                getTemplateFetchStatus === 'fetching' ? (
                  <div className="d-flex flex-column gap-3">
                    <ComponentLoadingSkeleton />
                    <ComponentLoadingSkeleton />
                  </div>
                ) : (
                  templateComponentIds.map((componentId) => (
                    <div key={componentId} className="mb-9">
                      <Component id={componentId} year={catalogYear} deferSave={true} />
                    </div>
                  ))
                )}
              </div>
              <div className="pt-2 mt-2 validation">
                <TemplateFormValidationRules />
              </div>
              <div className="pt-2 mt-2">
                <TemplateFormTextOptions />
              </div>
            </TabPanel>
          </div>
        </div>
        )}
        <br />
      </div>
      <Modal
        show={showPublishModal}
        onHide={() => setShowPublishModal(false)}
        centered
        size="xl"
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Review unpublished changes</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto', padding: '1.5rem 2.5rem' }}>
          {totalItemsChanged > 0 && (
            <>
              <h5 className="fw-bold mb-3">
                Items changed ({totalItemsChanged})
              </h5>
              {itemsChanged.sections > 0 && (
                <div className="mb-2">
                  <div
                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollege('__modal_sections__')}
                    role="button"
                  >
                    <FontAwesomeIcon
                      icon={collapsedColleges['__modal_sections__'] ? faChevronDown : faChevronRight}
                      style={{ width: '12px' }}
                    />
                    <span>Sections ({changedSections.length})</span>
                  </div>
                  {collapsedColleges['__modal_sections__'] && (
                    <div className="ps-1 py-1" style={{ fontSize: '0.9rem' }}>
                      {changedSections.map((change, idx) => (
                        <div
                          key={idx}
                          className={classNames(
                            'py-2 px-2 border-bottom border-gray-2 d-flex justify-content-between align-items-center',
                            change.status === 'Removed' ? 'bg-danger-subtle' : idx % 2 === 0 && 'bg-gray-1'
                          )}
                        >
                          <span className="fw-bold">{change.name}</span>
                          <span
                            className={classNames(
                              'text-nowrap',
                              change.status === 'Removed'
                                ? 'text-danger'
                                : 'text-gray-6'
                            )}
                          >
                            {change.status === 'Removed' && (
                              <FontAwesomeIcon
                                icon={faTrash}
                                className="me-1"
                                style={{ width: '12px' }}
                              />
                            )}
                            {change.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {itemsChanged.validationRules > 0 && (
                <div className="mb-2">
                  <div
                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollege('__modal_validation__')}
                    role="button"
                  >
                    <FontAwesomeIcon
                      icon={collapsedColleges['__modal_validation__'] ? faChevronDown : faChevronRight}
                      style={{ width: '12px' }}
                    />
                    <span>Validation Rules ({changedValidationFields.settings.length + changedValidationFields.rules.reduce((sum, r) => sum + r.changes.length, 0)})</span>
                  </div>
                  {collapsedColleges['__modal_validation__'] && (
                    <div className="ps-1 py-1" style={{ fontSize: '0.9rem' }}>
                      {changedValidationFields.settings.length > 0 && (
                        <>
                          {changedValidationFields.settings.map((item, idx) => (
                            <div key={idx} className={classNames('d-flex justify-content-between py-2 px-2 border-bottom border-gray-2', idx % 2 === 0 && 'bg-gray-1')}>
                              <span>{item.label}</span>
                              <span>{item.value}</span>
                            </div>
                          ))}
                        </>
                      )}
                      {changedValidationFields.rules.map((rule) => (
                        <div key={rule.category?.value}>
                          <div className="fw-bold mt-2 mb-1">
                            {rule.category?.label} ({rule.category?.value})
                          </div>
                          {rule.changes.map((change, idx) => (
                            <div key={idx} className={classNames('d-flex justify-content-between py-2 px-2 border-bottom border-gray-2', idx % 2 === 0 && 'bg-gray-1')}>
                              <span>{change.label}</span>
                              <span>{change.value}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {itemsChanged.textOptions > 0 && (
                <div className="mb-2">
                  <div
                    className="d-flex align-items-center gap-2 py-2 border-bottom"
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggleCollege('__modal_textoptions__')}
                    role="button"
                  >
                    <FontAwesomeIcon
                      icon={collapsedColleges['__modal_textoptions__'] ? faChevronDown : faChevronRight}
                      style={{ width: '12px' }}
                    />
                    <span>Text Options ({textOptionsChangeCount})</span>
                  </div>
                  {collapsedColleges['__modal_textoptions__'] && (
                    <div className="ps-1 py-1" style={{ fontSize: '0.9rem' }}>
                      {changedTextOptions.map((group, idx) => (
                        <div key={idx} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">{group.groupName}</span>
                            {group.groupStatus && (
                              <span
                                className={
                                  group.groupStatus === 'Removed'
                                    ? 'text-danger'
                                    : 'text-gray-6'
                                }
                              >
                                {group.groupStatus === 'Removed' && (
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="me-1"
                                    style={{ width: '12px' }}
                                  />
                                )}
                                {group.groupStatus}
                              </span>
                            )}
                          </div>
                          {group.options.map((opt, optIdx) => (
                            <div
                              key={optIdx}
                              className={classNames(
                                'py-2 px-2 border-bottom border-gray-2 d-flex justify-content-between align-items-start',
                                opt.status === 'Removed' ? 'bg-danger-subtle' : optIdx % 2 === 0 && 'bg-gray-1'
                              )}
                            >
                              <span className="pe-3">{stripHtmlTags(opt.text)}</span>
                              <span
                                className={classNames(
                                  'text-nowrap',
                                  opt.status === 'Removed'
                                    ? 'text-danger'
                                    : 'text-gray-6'
                                )}
                              >
                                {opt.status === 'Removed' && (
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="me-1"
                                    style={{ width: '12px' }}
                                  />
                                )}
                                {opt.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="mb-4" />
            </>
          )}
          <h5 className="fw-bold mb-3">Checksheets to be updated</h5>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            These checksheets will be updated after template changes are
            published.
          </p>
          <div className="fw-bold mb-2">
            {templateYear || catalogYear} ({checksheetCount} total checksheets)
          </div>
          {Object.entries(checksheetsByCollege).map(([college, programs]) => (
            <div key={college} className="mb-1">
              <div
                className={classNames(
                  'd-flex align-items-center gap-2 py-2 border-bottom college-hover-link',
                  collapsedColleges[college] && 'text-maroon'
                )}
                style={{ cursor: 'pointer' }}
                onClick={() => toggleCollege(college)}
                role="button"
              >
                <FontAwesomeIcon
                  icon={
                    collapsedColleges[college]
                      ? faChevronDown
                      : faChevronRight
                  }
                  style={{ width: '12px' }}
                />
                <span className="fw-bold">
                  {college} ({programs.length})
                </span>
              </div>
              {collapsedColleges[college] && (
                <div className="ps-4">
                  {programs.map((program, idx) => (
                    <div
                      key={idx}
                      className="py-1"
                      style={{ fontSize: '0.9rem' }}
                    >
                      {program.programName}
                      {program.degreeShort ? `, ${program.degreeShort}` : ''} (
                      {program.acadplan}
                      {program.subplan ? `/${program.subplan}` : ''})
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            label="Discard all changes"
            onClick={() => {
              if (templatePastState.length > 0) {
                const original = templatePastState[0];
                dispatch(
                  setAll({
                    templateId: original.templateId,
                    year: original.year,
                    templateName: original.templateName,
                    templateProgramType: original.templateProgramType,
                    templateSettings: original.templateSettings,
                    templateComponentIds: original.templateComponentIds,
                    templateComponentObjects: original.templateComponentObjects,
                    templateValidationList: original.templateValidationList,
                    templateTextOptions: original.templateTextOptions,
                  })
                );
                dispatch(clearHistory());
                setTemporaryName(original.templateName);
              }
              setShowPublishModal(false);
            }}
            color="dark"
            size="small"
          />
          <Button
            label="Publish changes"
            onClick={async () => {
              // Save modified components to database
              if (templatePastState.length > 0) {
                const original = templatePastState[0];
                const origObjects = original.templateComponentObjects || {};
                const token = await getAccessToken();
                for (const compId of templateComponentIds) {
                  if (
                    origObjects[compId] &&
                    JSON.stringify(origObjects[compId]) !==
                      JSON.stringify(templateComponentObjects?.[compId])
                  ) {
                    try {
                      const details = templateComponentObjects[compId]?.details;
                      if (details) {
                        const jsonData = JSON.parse(details);
                        await componentUpdate({ id: compId, jsonData, token });
                        console.log(`[Publish] Updated component: ${compId}`);
                      }
                    } catch (err) {
                      console.error(`[Publish] Error updating component ${compId}:`, err);
                    }
                  }
                }
              }
              handleSaveTemplate();
              dispatch(clearHistory());
              setShowPublishModal(false);
            }}
            color="gold"
            size="small"
          />
        </Modal.Footer>
      </Modal>
      <Modal show={showDefinitions} onHide={() => setShowDefinitions(false)}>
        <StatusDefinitions
          showNoChecksheet={true}
          handleClose={() => setShowDefinitions(false)}
        />
      </Modal>
    </>
  );
};

export default TemplateForm;
